import React, { useState, useEffect } from "react";
import { AiOutlineEye, AiOutlinePlus, AiOutlineSearch } from "react-icons/ai";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth } from "../../context/Auth";
import { FaUsers, FaUserTimes } from "react-icons/fa";
import AddGroupMember from "./AddGroupMember";

const GroupMembers = ({ activeTab, group, groupId, fetchGroupDetails }) => {
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [togglingMemberId, setTogglingMemberId] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchGroupMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await requestWithAuth(
        `/groups/group-members/${groupId}/?status=${statusFilter}&search=${debouncedSearch}&page=${currentPage}&size=${pageSize}`,
        "GET"
      );

      let filteredMembers = response.data;

      setGroupMembers(filteredMembers?.results);
      setTotalMembers(filteredMembers.count);
      setTotalPages(Math.ceil(filteredMembers.count / pageSize));
      setLoadingMembers(false);
    } catch (error) {
      console.error("Failed to fetch group members:", error);
      toast.error("Failed to load group members");
      setLoadingMembers(false);
    }
  };
  // Load group members
  useEffect(() => {
    

    if (group && activeTab === "members") {
      fetchGroupMembers();
    }
  }, [group, debouncedSearch, statusFilter, currentPage, pageSize, activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const handleRemoveMember = async (memberId) => {
    const result = await Swal.fire({
      title: "Remove Member?",
      text: "This member will be removed from the group",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove!",
    });

    if (result.isConfirmed) {
      try {
        
          setRemovingMemberId(memberId);
          await requestWithAuth(`/groups/group-member/${memberId}/`, 'DELETE');  
          toast.success("Member removed from group successfully!");
          fetchGroupMembers();
        
      } catch (error) {
        toast.error(error?.response?.data?.message);
        setRemovingMemberId(null);
      }
    }
  };


  const handleToggleMemberStatus = async (member) => {
    const newStatus = member.status === "Active" ? "Inactive" : "Active";
    try {
      setTogglingMemberId(member.id);
      await requestWithAuth(`/groups/group-member/${member.id}/`, "PUT", {
        status: newStatus,
      });

      fetchGroupDetails();

      setTimeout(() => {
        setGroupMembers((prev) =>
          prev.map((m) =>
            m.id === member.id ? { ...m, status: newStatus } : m
          )
        );
        toast.success(`Member status updated to ${newStatus}`);
        setTogglingMemberId(null);
      }, 1000);
    } catch (error) {
      console.error("Failed to update member status:", error);
      toast.error("Failed to update member status");
      setTogglingMemberId(null);
    }
  };

  const getInitials = (fullName) => {
    const names = fullName.split(" ");
    return `${names[0]?.[0] || ""}${names[1]?.[0] || ""}`.toUpperCase();
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case "elder":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "deacon":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "member":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div>
      {/* Section Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Group Members</h3>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative">
              <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            {/* Add Members Button */}
            <button
              onClick={() => setShowAddMemberModal(true)}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <AiOutlinePlus className="text-lg" />
              Add Members
            </button>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="overflow-x-auto">
        {loadingMembers ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner />
              <p className="text-gray-600 text-sm">Loading members...</p>
            </div>
          </div>
        ) : groupMembers?.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                  Member
                </th>
                <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                  Position
                </th>
                <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                  Status
                </th>
                
                <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {groupMembers?.map((member) => (
                <tr
                  key={member.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {member.profile_picture ? (
                          <img
                            src={member.profile_picture}
                            alt=""
                            className="object-cover h-full w-full rounded-full"
                          />
                        ) : (
                          getInitials(member.full_name)
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {member.full_name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {member.member_church_id}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(
                        member.position
                      )}`}
                    >
                      {member.position}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <label
                      htmlFor={`toggle-${member.id}`}
                      className="flex items-center cursor-pointer"
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          id={`toggle-${member.id}`}
                          className="sr-only"
                          checked={member.status === "Active"}
                          onChange={() => handleToggleMemberStatus(member)}
                          disabled={togglingMemberId === member.id}
                        />
                        <div
                          className={`block w-10 h-6 rounded-full ${
                            member.status === "Active"
                              ? "bg-green-400"
                              : "bg-gray-300"
                          }`}
                        ></div>
                        <div
                          className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform ${
                            member.status === "Active" ? "translate-x-full" : ""
                          }`}
                        ></div>
                      </div>
                      <div
                        className={`ml-3 text-sm font-medium ${
                          member.status === "Active"
                            ? "text-green-700"
                            : "text-red-700"
                        }`}
                      >
                        {togglingMemberId === member.id
                          ? "Updating..."
                          : member.status}
                      </div>
                    </label>
                  </td>
                  
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-1">
                     
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={removingMemberId === member.id}
                        className={`p-2 rounded-lg transition-colors ${
                          removingMemberId === member.id
                            ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                            : "text-red-600 hover:bg-red-50"
                        }`}
                        title="Remove from Group"
                      >
                        {removingMemberId === member.id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <FaUserTimes className="text-lg" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaUsers className="text-gray-400 w-12 h-12" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {search || statusFilter !== "all"
                ? "No members found"
                : "No members in this group"}
            </h3>
            <p className="text-gray-500 text-center mb-6 max-w-md">
              {search || statusFilter !== "all"
                ? "Try adjusting your search or filters."
                : "Start building your group by adding members."}
            </p>
            {!search && statusFilter === "all" && (
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
              >
                <AiOutlinePlus className="text-lg" />
                Add First Member
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {groupMembers?.length > 0 && totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {(currentPage - 1) * pageSize + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * pageSize, totalMembers)}
              </span>{" "}
              of <span className="font-medium">{totalMembers}</span> members
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm rounded border ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm rounded border ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddMemberModal && (
        <AddGroupMember
          groupId={groupId}
          setShowAddMemberModal={setShowAddMemberModal}
          showAddMemberModal={showAddMemberModal}
        />
      )}
    </div>
  );
};

export default GroupMembers;
